import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, message, Progress, Space, Card } from 'antd';
import firebase from '../../../firebase';

const ItemStockSync = ({ db }) => {
    const [syncOrgs, setSyncOrgs] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);

    const [tsReindexing, setTsReindexing] = useState(false);
    const [tsProgress, setTsProgress] = useState(0);
    const [tsPaused, setTsPaused] = useState(false);
    const [tsStopped, setTsStopped] = useState(false);
    const [tsTotalProcessed, setTsTotalProcessed] = useState(0);
    const [tsCurrentOrg, setTsCurrentOrg] = useState("");

    const tsPausedRef = useRef(false);
    const tsStoppedRef = useRef(false);

    useEffect(() => { tsPausedRef.current = tsPaused; }, [tsPaused]);
    useEffect(() => { tsStoppedRef.current = tsStopped; }, [tsStopped]);

    const waitIfPaused = async (pRef, sRef) => {
        while (pRef.current && !sRef.current) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    const fetchOrgs = async () => {
        setSyncLoading(true);
        try {
            const snapshot = await db.collection('organizations').where('maker', '==', true).get();
            const orgsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSyncOrgs(orgsData.filter(org => org.buyers && org.buyers.length > 0));
        } catch (error) {
            message.error("Failed to fetch organizations");
        }
        setSyncLoading(false);
    };

    useEffect(() => {
        fetchOrgs();
    }, []);

    const reindexStockToTypesense = async () => {
        setTsReindexing(true);
        setTsStopped(false);
        setTsPaused(false);
        tsStoppedRef.current = false;
        tsPausedRef.current = false;
        setTsProgress(0);
        setTsTotalProcessed(0);

        try {
            message.loading({ content: "Resetting Items Stock collection...", key: "tsReset" });
            const resetFn = firebase.functions().httpsCallable('resetItemsStockTypeSense');
            await resetFn();
            message.success({ content: "Collection reset. Starting reindex...", key: "tsReset", duration: 2 });

            const reindexFn = firebase.functions().httpsCallable('addItemStockAllDataToTypeSense');
            
            // Calculate total tasks (total number of seller-buyer pairs)
            let totalTasks = 0;
            syncOrgs.forEach(org => {
                if (org.buyers) totalTasks += org.buyers.length;
            });

            if (totalTasks === 0) {
                message.info("No tasks to perform");
                setTsReindexing(false);
                return;
            }

            let taskCounter = 0;

            for (const org of syncOrgs) {
                if (tsStoppedRef.current) break;
                await waitIfPaused(tsPausedRef, tsStoppedRef);
                if (tsStoppedRef.current) break;

                const sellerId = org.id; // Maker is the seller

                for (const buyerEntity of org.buyers) {
                    if (tsStoppedRef.current) break;
                    await waitIfPaused(tsPausedRef, tsStoppedRef);
                    if (tsStoppedRef.current) break;

                    const buyerId = buyerEntity.organization;
                    const buyerName = buyerEntity.name || "";
                    setTsCurrentOrg(`Maker: ${org.name || sellerId} | Buyer: ${buyerName || buyerId}`);
                    
                    let hasMoreInOrg = true;
                    let lastId = null;

                    while (hasMoreInOrg) {
                        if (tsStoppedRef.current) break;
                        await waitIfPaused(tsPausedRef, tsStoppedRef);
                        if (tsStoppedRef.current) break;

                        const result = await reindexFn({ buyerId, sellerId, lastId });
                        const { data } = result;

                        if (data.success) {
                            hasMoreInOrg = data.hasMore;
                            lastId = data.nextLastId;
                            
                            const processedInBatch = data.count || 0;
                            setTsTotalProcessed(prev => prev + processedInBatch);
                            
                            console.log(`Typesense Stock Sync: Seller ${sellerId} Buyer ${buyerId} | Processed ${processedInBatch} items. Total: ${tsTotalProcessed + processedInBatch}`);
                        } else {
                            throw new Error(data.message || "Unknown error during reindexing");
                        }
                    }

                    taskCounter++;
                    setTsProgress(Math.round((taskCounter / totalTasks) * 100));
                }
            }

            if (!tsStoppedRef.current) {
                message.success(`Typesense items_stock reindexing completed! Total items: ${tsTotalProcessed}`);
            } else {
                message.warning("Typesense items_stock reindexing stopped.");
            }

        } catch (error) {
            console.error("Typesense stock reindex error:", error);
            message.error(`Reindexing failed: ${error.message}`);
        }

        setTsReindexing(false);
    };

    const columnsOrgs = [
        { title: 'Maker ID', dataIndex: 'id', key: 'id' },
        { title: 'Maker Name', dataIndex: 'name', key: 'name' },
        { title: 'Associated Buyers', key: 'buyers', render: (_, record) => (
            <div>
                {record.buyers && record.buyers.map(b => (
                    <div key={b.organization} style={{ fontSize: '12px' }}>
                        • {b.name} ({b.organization})
                    </div>
                ))}
            </div>
        )}
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <Card title="Typesense Full Reindex (items_stock)" size="small">
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 500 }}>Sync all items_stock items (Makers to Buyers) to Typesense</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            {tsReindexing && `Current: ${tsCurrentOrg} | Total Processed: ${tsTotalProcessed}`}
                        </div>
                    </div>
                    <Space>
                        {tsReindexing ? (
                            <>
                                <Button onClick={() => setTsPaused(!tsPaused)}>{tsPaused ? "Resume" : "Pause"}</Button>
                                <Button danger onClick={() => { setTsStopped(true); tsStoppedRef.current = true; }}>Stop</Button>
                            </>
                        ) : (
                            <Button type="primary" onClick={reindexStockToTypesense}>Start Typesense Reindex</Button>
                        )}
                    </Space>
                </div>
                {tsReindexing && <Progress percent={tsProgress} status={tsPaused ? "active" : "normal"} style={{ marginBottom: '16px' }} />}
            </Card>

            <Table dataSource={syncOrgs} columns={columnsOrgs} rowKey="id" loading={syncLoading} pagination={{ pageSize: 15 }} />
        </div>
    );
};

export default ItemStockSync;
